import React, { useState, useEffect } from 'react';
import { usePerformance } from '../hooks/usePerformance';
import { usePWA } from '../hooks/usePWA';
import { 
  Activity, 
  Zap, 
  HardDrive, 
  Network, 
  AlertTriangle, 
  CheckCircle,
  RefreshCw,
  Download,
  Wifi,
  WifiOff
} from 'lucide-react';

const PerformanceDashboard = () => {
  const { metrics, getMetrics, resetMetrics } = usePerformance();
  const { isOnline, registration } = usePWA();
  const [isVisible, setIsVisible] = useState(false);
  const [performanceData, setPerformanceData] = useState(null);

  useEffect(() => {
    const updateMetrics = () => {
      const data = getMetrics();
      setPerformanceData(data);
    };

    updateMetrics();
    const interval = setInterval(updateMetrics, 5000);

    return () => clearInterval(interval);
  }, [getMetrics]);

  // Show/hide dashboard with Ctrl+Shift+P
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'P') {
        setIsVisible(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!isVisible || process.env.NODE_ENV === 'production') {
    return null;
  }

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTime = (ms) => {
    return `${ms.toFixed(2)}ms`;
  };

  return (
    <div className="fixed top-4 right-4 z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Performance Dashboard</h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={resetMetrics}
            className="p-1 text-gray-400 hover:text-gray-600"
            title="Reset Metrics"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <button
            onClick={() => setIsVisible(false)}
            className="p-1 text-gray-400 hover:text-gray-600"
            title="Close"
          >
            ×
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {/* Connection Status */}
        <div className="flex items-center space-x-2">
          {isOnline ? (
            <Wifi className="h-4 w-4 text-green-600" />
          ) : (
            <WifiOff className="h-4 w-4 text-red-600" />
          )}
          <span className="text-sm font-medium">
            {isOnline ? 'Online' : 'Offline'}
          </span>
        </div>

        {/* Performance Metrics */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-1">
              <Zap className="h-4 w-4 text-indigo-600" />
              <span className="text-sm font-medium text-gray-700">Load Time</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">
              {formatTime(metrics.loadTime)}
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-1">
              <Activity className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-gray-700">Render Time</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">
              {formatTime(metrics.renderTime)}
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-1">
              <Network className="h-4 w-4 text-purple-600" />
              <span className="text-sm font-medium text-gray-700">Requests</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">
              {metrics.networkRequests}
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-1">
              {metrics.errors > 0 ? (
                <AlertTriangle className="h-4 w-4 text-red-600" />
              ) : (
                <CheckCircle className="h-4 w-4 text-green-600" />
              )}
              <span className="text-sm font-medium text-gray-700">Errors</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">
              {metrics.errors}
            </p>
          </div>
        </div>

        {/* Memory Usage */}
        {performanceData?.memory && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-2">
              <HardDrive className="h-4 w-4 text-orange-600" />
              <span className="text-sm font-medium text-gray-700">Memory Usage</span>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span>Used:</span>
                <span>{formatBytes(performanceData.memory.used)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Total:</span>
                <span>{formatBytes(performanceData.memory.total)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Limit:</span>
                <span>{formatBytes(performanceData.memory.limit)}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div 
                  className="bg-orange-600 h-2 rounded-full"
                  style={{ 
                    width: `${(performanceData.memory.used / performanceData.memory.limit) * 100}%` 
                  }}
                ></div>
              </div>
            </div>
          </div>
        )}

        {/* Service Worker Status */}
        {registration && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-1">
              <Download className="h-4 w-4 text-indigo-600" />
              <span className="text-sm font-medium text-gray-700">Service Worker</span>
            </div>
            <p className="text-sm text-green-600">Active</p>
          </div>
        )}

        {/* Performance Tips */}
        <div className="bg-indigo-50 rounded-lg p-3">
          <h4 className="text-sm font-medium text-indigo-900 mb-2">Performance Tips</h4>
          <ul className="text-xs text-indigo-800 space-y-1">
            <li>• Use lazy loading for images</li>
            <li>• Enable service worker caching</li>
            <li>• Minimize bundle size</li>
            <li>• Use virtual scrolling for large lists</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PerformanceDashboard;
